import React from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import { useAuth0 } from "@auth0/auth0-react";
import { isNativeIapSupported } from "src/services/iap";
import { getProdFrontendFallbackUrl } from "src/config/frontendUrls";
import { trackSignupScreenSeenAt } from "src/utils/analyticsCapFirebase";
import { isIOS, isAndroid, mobile, isMobileWebBrowser } from "src/utils/platformUtils";
import { frontEnvNumber } from "src/services/runtimeFrontConfig";
interface FooterSignUpProps {
    isLoggedInAuth0: boolean;
    setIsPaymentOpen?: (open: boolean) => void;
    setInitialPaymentView?: (view: string) => void;
    onDismiss?: () => void;
}

// Fixed bottom CTA: Sign Up (non-logged) or Upgrade (logged free). Logic mirrored from UpgradeSubscription* without importing those components.
const FooterSignUp: React.FC<FooterSignUpProps> = ({
    isLoggedInAuth0,
    setIsPaymentOpen,
    setInitialPaymentView,
    onDismiss,
}) => {
    const { loginWithRedirect } = useAuth0();
    const isNativePlatform = Capacitor.isNativePlatform();
    const isHybridPlatform = isPlatform("hybrid");
    const isRunningNatively = isNativePlatform || isHybridPlatform;
    const isMobile = mobile || isMobileWebBrowser();
    const isMobileWeb = isMobileWebBrowser();
    const loggedFreeCreditsOneTime = frontEnvNumber("REACT_APP_LOGGED_FREE_CREDITS_ONE_TIME", 50);

    // Function to open web app in Capacitor Browser for native platforms (logged free upgrade path)
    const openWebAppInBrowser = async (path: string = "/home", queryParams: string = "") => {
        if (!isRunningNatively) {
            return false;
        }

        try {
            const baseUrl = process.env.REACT_APP_FRONTEND_URI_WEB || getProdFrontendFallbackUrl();
            const cleanBaseUrl = baseUrl.replace(/\/$/, "").replace(/\/home$/, "");
            const fullUrl = `${cleanBaseUrl}${path}${queryParams ? `?${queryParams}` : ""}`;

            await Browser.open({ url: fullUrl, presentationStyle: "popover" });
            return true;
        } catch (error) {
            console.error("FooterSignUp: Error opening Capacitor Browser:", error);
            return false;
        }
    };

    // Logged free: same upgrade flow as UpgradeSubscriptionFree
    const handleUpgradeClick = async () => {
        if (isNativeIapSupported()) {
            if (setInitialPaymentView && setIsPaymentOpen) {
                setInitialPaymentView("manageMembership");
                setIsPaymentOpen(true);
            }
            return;
        }

        if (isRunningNatively) {
            const opened = await openWebAppInBrowser("/home", "openPayment=manageMembership&fromApp=true");
            if (opened) return;
        }

        if (setInitialPaymentView && setIsPaymentOpen) {
            setInitialPaymentView("manageMembership");
            setIsPaymentOpen(true);
        }
    };

    // Non-logged: same Auth0 signup flow as UpgradeSubscriptionNonLogged
    const handleSignUpForFree = async (event: React.MouseEvent) => {
        event.stopPropagation();
        await trackSignupScreenSeenAt("locked_feature", "footer_sign_up");
        await loginWithRedirect({
            async openUrl(url) {
                await Browser.open({
                    url,
                    windowName: "_self",
                });
            },
        });
    };

    const buttonLabel = isLoggedInAuth0
        ? "Upgrade"
        : `Sign Up For ${loggedFreeCreditsOneTime} Free Credits`;
    const helperText = isLoggedInAuth0
        ? "Want more credits? Upgrade today."
        : null;

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 20, // Above section buttons / teams accordion (z 10–11); below HelpMenu/Welcome drawers
                pointerEvents: "none",
            }}
        >
            <Box
                sx={{
                    pointerEvents: "auto",
                    position: "relative",
                    // width: "calc(100% - 30px)",
                    // Fixed px inset on mobile (not a viewport %); desktop sizes to content so DevTools resize does not shrink the card
                    width: isMobile ? "calc(100% - 32px)" : "fit-content",
                    maxWidth: isMobile ? "400px" : "none",
                    // Non-logged desktop: only a long button — minWidth keeps the card (logged already has helper text)
                    minWidth: !isMobile && !isLoggedInAuth0 ? "420px" : undefined,
                    mx: "auto",
                    mb: mobile ? "max(env(safe-area-inset-bottom), 8px)" : 1.5,
                    bgcolor: PureLightThemeCaaS.colors.alpha.black[100],
                    border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    borderRadius: 1,
                    boxShadow: `0 0 12px 1px ${PureLightThemeCaaS.colors.alpha.orange[70]}`,
                    // borderTop: `0.5px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    px: 0.5,
                    pr: isLoggedInAuth0 ? 6 : 0, // room for the visual close icon
                    // pt: mobile ? "max(env(safe-area-inset-bottom), 9px)" : isMobileWeb ? "14px" : "16px",
                    // pb: mobile ? "max(env(safe-area-inset-bottom), 0px)" : isMobileWeb ? "14px" : "16px",
                    pt: isMobile ? "14px" : "16px",
                    pb: isMobile ? "14px" : "16px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: isMobile ? 1 : 2,
                }}
            >
                {/* Close: snoozes FooterSignUp in user preferences (see HomePage handleFooterSignUpDismiss) */}
                <IconButton
                    aria-label="dismiss signup"
                    disableRipple
                    onClick={(event) => {
                        event.stopPropagation();
                        onDismiss?.();
                    }}
                    sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        p: 0.5,
                        color: PureLightThemeCaaS.colors.alpha.white[100],
                    }}
                >
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
                {/* only render helper text when the user is already logged in  */}
                {isLoggedInAuth0 && (<Typography
                    variant="body1"
                    sx={{
                        color: PureLightThemeCaaS.colors.alpha.white[100],
                        fontSize: isMobile ? "14px" : "16px",
                        fontWeight: "bold",
                        lineHeight: 1.2,
                        pl: 1,
                        // flex: 1,
                    }}
                >
                    {helperText}
                </Typography>)}
                <Button
                    variant="contained"
                    disableRipple
                    onClick={isLoggedInAuth0 ? handleUpgradeClick : handleSignUpForFree}
                    sx={{
                        flexShrink: 0,
                        bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                        color: PureLightThemeCaaS.colors.alpha.white[100],
                        borderRadius: 2,
                        px: 2,
                        py: 0,
                        textTransform: "none",
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: "bold",
                        boxShadow: "none",
                        "&:hover": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[100], boxShadow: "none" },
                        "&:active": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[100], boxShadow: "none" },
                        "&.Mui-focusVisible": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[100] },
                    }}
                >
                    {buttonLabel}
                </Button>
            </Box>
        </Box >
    );
};

export default FooterSignUp;
